import React, { useEffect, useState } from "react";

import { useNotify } from "../../context/NotificationContext";
import { fetchStudentTeachers } from "../../services/studentPortalService/studentPortalService";
import type { StudentTeacherDetails } from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import {
  EmptyTeachers,
  EmptyTeachersText,
  EmptyTeachersTitle,
  TeacherChevron,
  TeacherList,
  TeacherListAside,
  TeacherListLink,
  TeacherListMain,
  TeacherMeta,
  TeacherName,
  TeacherTelegramStatus,
  TeachersLoading,
  TeachersPageLead,
  TeachersPageShell,
  TeachersPageTitle,
} from "./StudentTeachersPage.styles";

const pluralCourses = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "курс";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "курса";
  return "курсов";
};

const StudentTeachersPage: React.FC = () => {
  const [teachers, setTeachers] = useState<StudentTeacherDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useNotify();

  useEffect(() => {
    let active = true;
    fetchStudentTeachers()
      .then((data) => active && setTeachers(data))
      .catch((error) => {
        logger.error("Ошибка загрузки каталога преподавателей:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось загрузить каталог курсов",
          life: 3000,
        });
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [toast]);

  return (
    <TeachersPageShell>
      <StudentBreadcrumbs
        items={[
          { label: "Мои курсы", to: "/student/courses" },
          { label: "Каталог курсов" },
        ]}
      />
      <TeachersPageTitle>Каталог курсов</TeachersPageTitle>
      <TeachersPageLead>
        Выберите преподавателя или школу, чтобы посмотреть доступные курсы и подключить Telegram-бот.
      </TeachersPageLead>

      {loading ? (
        <TeachersLoading>Загружаем каталог…</TeachersLoading>
      ) : teachers.length === 0 ? (
        <EmptyTeachers>
          <EmptyTeachersTitle>Каталог пока пуст</EmptyTeachersTitle>
          <EmptyTeachersText>
            Добавьте каталог преподавателя по его личной ссылке, и он появится здесь.
          </EmptyTeachersText>
        </EmptyTeachers>
      ) : (
        <TeacherList>
          {teachers.map((teacher) => {
            const activeCourses = teacher.courses.filter((course) => course.has_access).length;
            const telegramStatus = !teacher.telegram.available
              ? "Telegram недоступен"
              : teacher.telegram.connected
                ? "Telegram подключён"
                : "Telegram доступен";

            return (
              <TeacherListLink key={teacher.id} to={`/student/catalog/${teacher.id}`}>
                <TeacherListMain>
                  <TeacherName>{teacher.organization_name}</TeacherName>
                  <TeacherMeta>
                    {teacher.courses.length} {pluralCourses(teacher.courses.length)} в каталоге
                    {activeCourses > 0 ? ` · ${activeCourses} с открытым доступом` : ""}
                  </TeacherMeta>
                </TeacherListMain>
                <TeacherListAside>
                  <TeacherTelegramStatus $connected={teacher.telegram.connected}>
                    {telegramStatus}
                  </TeacherTelegramStatus>
                  <TeacherChevron aria-hidden="true" />
                </TeacherListAside>
              </TeacherListLink>
            );
          })}
        </TeacherList>
      )}
    </TeachersPageShell>
  );
};

export default StudentTeachersPage;
